import { connect } from 'react-redux';
import {
  setFilesUploaded,
  updateUploadingError,
  useTableContentFlow,
  useTextFileContentFlow
} from './_actions';

import { LOTTIE_LOADING } from 'constants/images';
import { InputMaybe, Maybe, Scalars, UploadedFile } from 'gql/graphql';
import { useTranslations } from 'next-intl';
import { useTranslationRequestFileUpload } from 'providers/TranslationRequests/useTranslationRequestFileUpload';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import UploadProgressBar from './UploadProgressBar';

enum PHASES {
  uploading = 'uploading',
  parsing = 'parsing',
  downloading = 'downloading'
}

const PHASE_TITLES = {
  [PHASES.uploading]: 'ordering_form.uploader.uploading_document',
  [PHASES.parsing]: 'ordering_form.uploader.parsing_document',
  [PHASES.downloading]: 'ordering_form.uploader.downloading_document'
};

export const WaitingState: FC<
  PropsWithChildren<{
    title: string;
  }>
> = ({ title, children }) => {
  const t = useTranslations();

  return (
    <div className="ordering_form__content_frame rounded shadow-custom">
      <h2>{t(title as any)}</h2>
      <div className="grid place-content-center">
        <iframe width="600" height="300" src={LOTTIE_LOADING} />
      </div>
      {children}
    </div>
  );
};

// 1. On mount uploads the file via /file_translation/upload
// 2. Polls the progress of its parsing pollParsingProgress
// 3. Gets the resulting file data with downloadFileData

interface IUploadingFlow {
  filesToUpload?: File[];
  metadata?: InputMaybe<Scalars['JSON']>[];
  csvDelimiter?: string;
  filesUploaded?: Maybe<Array<UploadedFile> | undefined>;
  setFilesUploaded: (filesUploaded: Maybe<Array<UploadedFile> | undefined>) => void;
  updateUploadingError: (error: string) => void;
  useTableContentFlow: () => void;
  useTextFileContentFlow: () => void;
}

const UploadingFlow: FC<IUploadingFlow> = ({
  filesToUpload = [],
  csvDelimiter,
  metadata = [],
  filesUploaded = [],
  updateUploadingError,
  setFilesUploaded
}) => {
  const t = useTranslations();

  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(PHASES.uploading);

  const { mutateAsync: translationRequestFileUpload } = useTranslationRequestFileUpload({});

  const uploadFile = useCallback(async () => {
    try {
      if (!loading) {
        setPhase(PHASES.parsing);
        setLoading(true);

        const response = await translationRequestFileUpload({
          files: filesToUpload,
          csvDelimiter,
          metadata
        });

        if (response.errors?.length) return updateUploadingError(t('messages.error.uploading'));

        setFilesUploaded(response?.files);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      return updateUploadingError(t('messages.error.uploading'));
    }
  }, [
    loading,
    metadata,
    translationRequestFileUpload,
    filesToUpload,
    csvDelimiter,
    updateUploadingError,
    t,
    setFilesUploaded
  ]);

  const debouncedMutation = useDebouncedCallback(uploadFile, 1);

  useEffect(() => {
    debouncedMutation();
  }, [debouncedMutation]);

  return (
    <WaitingState title={PHASE_TITLES[phase]}>
      {filesUploaded?.map((file, index) => (
        <UploadProgressBar file={file} key={(file?.uid ?? '') + index} />
      ))}
    </WaitingState>
  );
};

export default connect(
  ({ filesToUpload, csvDelimiter, metadata, filesUploaded }) => ({
    filesToUpload,
    metadata,
    csvDelimiter,
    filesUploaded
  }),
  {
    setFilesUploaded,
    updateUploadingError,
    useTableContentFlow,
    useTextFileContentFlow
  }
)(UploadingFlow);
